import React, { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker";
import JSZip from "jszip";
import FileUploader from "./FileUploader";
import TitleHeading from "./TitleHeading";
import { FiDownload } from "react-icons/fi";
import { logOperationSuccess } from "../lib/analytics";

const PDFToJPGConverter = () => {
	const [files, setFiles] = useState([]);
	const [imagesArray, setImagesArray] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const scale = 3;

	useEffect(() => {
		if (files.length > 0) handleFileChange(files);
	}, [files]);

	const handleFileChange = async (data) => {
		const start = Date.now();
		setLoading(true);

		if (!data || data.length === 0) {
			setLoading(false);
			return;
		}

		const inputFileCount = data.length;
		const inputTotalBytes = Array.from(data).reduce(
			(sum, f) => sum + (f?.size || 0),
			0
		);

		const renderedImageFiles = [];

		try {
			for (const file of data) {
				const fileReader = new FileReader();

				// Read each file as an array buffer
				const fileArrayBuffer = await new Promise((resolve, reject) => {
					fileReader.onload = () => resolve(fileReader.result);
					fileReader.onerror = reject;
					fileReader.readAsArrayBuffer(file);
				});

				const typedArray = new Uint8Array(fileArrayBuffer);
				const pdf = await pdfjsLib.getDocument(typedArray).promise;

				let renderedImages = [];

				for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
					const page = await pdf.getPage(pageNum);

					// Set high resolution scale (e.g. 2 for double resolution)
					// const scale = 2.0; // Adjust scale for higher resolution
					const viewport = page.getViewport({ scale });

					const canvas = document.createElement("canvas");
					const context = canvas.getContext("2d");

					canvas.width = viewport.width;
					canvas.height = viewport.height;

					await page.render({
						canvasContext: context,
						viewport: viewport,
					}).promise;

					// Convert canvas to JPG image URL with high quality
					const imageData = canvas.toDataURL("image/jpeg", 1); // Quality set to 1.0 (high)
					renderedImages.push(imageData);
				}

				// Add rendered images of the current file to the global array
				renderedImageFiles.push({
					imageData: renderedImages,
					name: file.name || "unknown",
				});
				renderedImages = [];
			}

			// Set the images array in state
			setImagesArray((prev) =>
				prev.length === 0
					? renderedImageFiles
					: [...prev, ...renderedImageFiles]
			);
			setLoading(false);

			// ✅ log success (don’t block UI)
			logOperationSuccess({
				operation: "CONVERT",
				page: "/convert", // adjust to your route
				fileCount: inputFileCount,
				totalBytes: inputTotalBytes,
				outputType: "pdf-to-jpg",
				durationMs: Date.now() - start,
			}).catch(console.error);
		} catch (error) {
			console.error("Conversion error:", error);
			setLoading(false);
			// optional: show error UI/toast
		}
	};

	// Function to download all images as ZIP
	const downloadAllAsZip = async (images) => {
		if (images.length === 0) return;

		const zip = new JSZip();

		// Create a folder in the zip for images
		const imgFolder = zip.folder("images");

		// Iterate over all images and add them to the "images" folder in the zip
		images.forEach((img, index) => {
			if (img && img.includes("base64")) {
				const base64Data = img.split(",")[1]; // Strip out the base64 header
				const filename = img.filename || `page-${index + 1}.jpg`; // Use a default filename if none exists

				// Add image to the zip folder with the filename
				imgFolder.file(filename, base64ToBlob(base64Data), { binary: true });
			} else {
				console.warn(`Skipping image at index ${index}, missing valid data.`);
			}
		});

		if (zip.files) {
			const zipBlob = await zip.generateAsync({ type: "blob" });
			const zipUrl = URL.createObjectURL(zipBlob);

			const a = document.createElement("a");
			a.href = zipUrl;
			a.download = "";
			a.click();

			URL.revokeObjectURL(zipUrl);
		} else {
			alert("No valid images to download.");
		}
	};

	// Helper function to convert base64 to Blob
	const base64ToBlob = (base64) => {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: "image/png" });
	};

	return (
		<div className="relative overflow-hidden px-4">
			{error && (
				<p className=" absolute top-0 z-50 left-0 right-0 mx-5 py-5 text-center bg-red-700 rounded-lg text-lg font-bold">
					{error}

					<span
						onClick={() => setError("")}
						className="absolute top-2 right-2 cursor-pointer"
					>
						X
					</span>
				</p>
			)}
			<TitleHeading title="Convert PDF to Image" />
			<div className="relative mb-5 h-[150px]">
				{loading && (
					<p className="absolute top-0 w-full bg-slate-950 bg-opacity-70 h-[150px] flex justify-center items-center text-2xl">
						Converting...
					</p>
				)}

				<FileUploader
					onFilesAdded={(data) => {
						setError("");
						setFiles(data);
					}}
					multiple={false}
					disabled={loading}
					setErrorOccured={(data) => {
						setError(data);
					}}
				/>
			</div>

			<div className="overflow-y-scroll h-[500px] no-scrollbar overflow-hidden flex flex-col gap-3">
				{imagesArray.map((images, index) => (
					<div key={index} className="bg-slate-950 p-2">
						<div className="mb-2 flex justify-between gap-4">
							<h1>{images?.name.replace(/\.pdf$/i, ".jpg")}</h1>

							<button
								onClick={() => downloadAllAsZip(images.imageData)}
								className="flex gap-1 items-center font-bold"
							>
								<FiDownload fontWeight={50} className="font-bold" />

								<span>Download</span>
							</button>
						</div>

						<div className="relative flex items-center gap-2 overflow-x-scroll no-scrollbar">
							{images?.imageData?.map((imgSrc, i) => (
								<div
									key={i}
									className="min-w-[150px] max-w-[150px] bg-gray-300"
								>
									<img
										src={imgSrc}
										alt={`Page ${i + 1}`}
										width={"150px"}
										style={{ width: "100%" }}
										className="bg-slate-500 bg-opacity-50"
									/>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default PDFToJPGConverter;
